import React, { useEffect, useState } from 'react'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import {
  type ExposureStatisticData,
  type PortfolioExposureStatisticData
} from '../../models/exposure-statistic/exposure-statistics-types'
import { type GridAutosizeOptions, type GridColDef } from '@mui/x-data-grid-pro'
import WrappedDataGrid from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid'
import { MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'
import { createExposureStatisticRows } from '../../utils/view-specific/exposure-statistic/exposure-statistic-utils'
import {
  EXPOSURE_STATISTIC_MODE_OPTIONS_CONFIG, getExposureStatisticAutosizeOptions, getExposureStatisticColDef,
} from './exposure-statistic-data-grid-col-definitions'
import { isValidNumberString } from '../../utils/string-utils'

export interface ExposureStatisticDataGridRow extends GridRowModelUpdate, ExposureStatisticData {
  id: string
}

interface ExposureStatisticDataGridProps {
  portfolio: string
}

const ExposureStatisticDataGrid: React.FC<ExposureStatisticDataGridProps> = ({ portfolio }) => {
  const [mode, setMode] = useState<MODE_OPTIONS>(MODE_OPTIONS.NONE)

  const modeEndpoint: string | undefined = EXPOSURE_STATISTIC_MODE_OPTIONS_CONFIG.endpoint
  const sortModel = EXPOSURE_STATISTIC_MODE_OPTIONS_CONFIG.sortModel

  const { data, error, mutate } = useSWR<PortfolioExposureStatisticData, boolean>(
    modeEndpoint,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000
    }
  )

  const [columns, setColumns] = useState<GridColDef[]>([])
  const [autosizeOptions, setAutosizeOptions] = useState<GridAutosizeOptions>({})
  const [rows, setRows] = useState<ExposureStatisticDataGridRow[]>([])

  useEffect(() => {
    if (error === true || data === undefined) {
      setColumns([])
      setAutosizeOptions({})
      setRows([])
    } else {
      let maxLvg = Number.MIN_SAFE_INTEGER
      let minLvg = Number.MAX_SAFE_INTEGER
      for (const item of data[portfolio]) {
        const correctedTotalLvg = isValidNumberString(item.total_lvg) ? Number(item.total_lvg) : 0
        const correctedNetLvg = isValidNumberString(item.net_lvg) ? Number(item.net_lvg) : 0
        maxLvg = Math.max(maxLvg, correctedTotalLvg, correctedNetLvg)
        minLvg = Math.min(minLvg, correctedTotalLvg, correctedNetLvg)
      }
      const columns = getExposureStatisticColDef(maxLvg, minLvg)
      setColumns(columns)
      setAutosizeOptions(getExposureStatisticAutosizeOptions(columns))

      setRows(createExposureStatisticRows(data[portfolio]))
    }
  }, [data, error])

  return (
    <WrappedDataGrid
      gridData={{
        rows,
        columns,
        autosizeOptions,
        portfolio,
        sortModel,
        csvOptions: { fileName: portfolio + '_exposure_statistic' }
      }}
      gridMode={{
        mode,
        modeOptions: Object.values(MODE_OPTIONS).filter(mode => mode === 'none'),
        setMode,
      }}
      mutate={mutate}
    />
  )
}

export default ExposureStatisticDataGrid
